<template>
  <v-container>
    <v-row v-if="notification && notification.formatted">
      <v-col cols="4" class="pt-0">
        <v-row>
          <v-col class="text-h5">
            Alert Settings
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              :value="notification.formatted.name"
              label="Alert Name"
              :error-messages="errorMessageName"
              :maxlength="75"
              class="pt-0"
              :disabled="readOnly"
              @input="handleChangeName"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              :value="notification.formatted.frequency"
              :items="frequencies"
              item-value="adverb"
              item-text="display"
              label="Delivery Frequency"
              hide-details
              class="mt-2 pt-0"
              :disabled="readOnly"
              @change="handleChangeFrequency"
            ></v-select>
          </v-col>
        </v-row>
        <v-row
          v-if="
            !isCyberWeekly &&
            !isCalendarEvents &&
            notification.formatted.frequency &&
            notification.formatted.frequency.toUpperCase() === frequencyWeekly
          "
          row
          wrap
        >
          <v-col>
            <span class="hyp-time-day-label">Day of Week Delivered</span>
            <v-select
              :value="notification.formatted.dayOfWeek"
              :items="daysOfWeek"
              hide-details
              class="mt-0 pt-0"
              :disabled="readOnly"
              @change="handleChangeDayOfWeek"
            ></v-select>
          </v-col>
        </v-row>
        <v-row
          v-if="
            !isCyberWeekly &&
            !isCalendarEvents &&
            notification.formatted.frequency &&
            (notification.formatted.frequency.toUpperCase() ===
              frequencyDaily ||
              notification.formatted.frequency.toUpperCase() ===
                frequencyWeekly)
          "
          row
          wrap
        >
          <v-col>
            <span class="hyp-time-day-label">Time of Day Delivered</span>
            <TimeSelector
              :time="notification.formatted.timeOfDay"
              :show-utc="true"
              :read-only="readOnly"
              @change="handleChangeTime"
            ></TimeSelector>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              :value="notification.formatted.expiration"
              :items="expirations"
              label="Expiration"
              item-value="id"
              item-text="name"
              :messages="expirationMessage"
              :error-messages="errorMessageExpiration"
              class="mt-3 pt-0"
              :disabled="readOnly"
              @change="handleChangeExpiration"
            ></v-select>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="1"></v-col>
      <v-col>
        <v-row v-if="allowShareWithColleagues">
          <v-col>
            <v-checkbox
              v-model="shareWithColleagues"
              :value="true"
              label="Share this alert with colleagues"
              hide-details
              class="mt-0 pt-0"
              :disabled="readOnly"
            ></v-checkbox>
            <UserShareListEditor
              v-if="showSharedUsers"
              :value="notification.formatted.sharedList"
              :init="sharedWithInit"
              :return-object="true"
              :access-group-allowed="accessGroupAllowed"
              :enforce-essentials="enforceEssentials"
              width-class-name="col-11"
              class="hyp-shared-listing mt-3"
              @input="handleChangeSharedList"
            ></UserShareListEditor>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <span>Delivery Channels</span>
            <v-checkbox
              v-model="delivery"
              label="Email"
              value="email"
              class="mt-2 pt-0"
              :disabled="readOnly"
            ></v-checkbox>
            <v-checkbox
              v-model="delivery"
              label="Push"
              value="push"
              class="mt-0 pt-0"
              :disabled="readOnly"
            ></v-checkbox>

            <div>
              <v-checkbox
                v-model="delivery"
                label="Targets"
                value="target"
                class="pull-left mt-0 pt-0 mr-3"
                :disabled="readOnly || !targets || !targets.length"
              ></v-checkbox>

              <v-tooltip
                bottom
                v-if="!readOnly && (!targets || !targets.length)"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="blue" size="16"
                    >fas fa-info-circle</v-icon
                  >
                </template>
                <div class="target-tooltip">
                  Targets are used to send alerts to 3rd party services such as
                  Slack, WhatsApp and IFTTT. To create targets go to Settings in
                  the left side menu and then click the Communications tab. Once
                  you create a target you will be able to select the Targets
                  checkbox here and then select a specific target you've created
                  from a drop down.
                </div>
              </v-tooltip>

              <v-select
                v-if="targets && targets.length"
                :value="notification.formatted.targets"
                :disabled="
                  readOnly ||
                  !notification.formatted.delivery ||
                  !notification.formatted.delivery.includes('target')
                "
                placeholder="Select targets"
                :items="targets"
                item-text="name"
                item-value="targetId"
                multiple
                class="pull-left mt-0 pt-0 hyp-targets"
                @change="handleChangeTargets"
              >
                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-checkbox :input-value="active"></v-checkbox>
                    <v-icon
                      v-if="item.icon"
                      :title="item.targetType.toUpperCase()"
                      size="16"
                    >
                      {{ item.icon }}
                    </v-icon>
                    <span
                      v-if="!item.icon"
                      :title="item.targetType.toUpperCase()"
                      class="target-type-header"
                    >
                      {{ item.targetType }}
                    </span>
                    <span class="ml-3">{{ item.name }}</span>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0">
                    {{ numberOfTargetsSelected }} selected
                  </span>
                </template>
              </v-select>
              <div class="clearfix"></div>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="1"></v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashIsEqual from "lodash/isEqual";
import dayjs from "dayjs";

import { MODULE_PREFIX_NOTIFICATIONS } from "@/store";
import { GET_NOTIFICATION } from "@/store/actions.type";
import { mapGetters } from "vuex";
import { DAYS_OF_WEEK } from "@/common/constants/misc";
import { getFormattedDate, nowUtc } from "@/common/date.utilities";
import {
  NOTIFICATION_EXPIRATION_ID_NEVER,
  NOTIFICATION_EXPIRATION_OPTIONS,
  NOTIFICATION_FREQUENCY_OPTIONS,
  NOTIFICATION_FREQUENCY_IMMEDIATELY,
  NOTIFICATION_FREQUENCY_HOURLY,
  NOTIFICATION_FREQUENCY_DAILY,
  NOTIFICATION_FREQUENCY_WEEKLY,
  NOTIFICATION_FREQUENCY_MONTHLY,
  NOTIFICATION_TRIPWIRE_TYPE_RISK_RATING,
  NOTIFICATION_TRIPWIRE_TYPE_EVACUATION,
  NOTIFICATION_TRIPWIRE_TYPE_HOTSPOT,
  NOTIFICATION_TRIPWIRE_TYPE_PULSE,
  NOTIFICATION_TRIPWIRE_TYPE_PLANNED_EVENT,
  NOTIFICATION_BRIEFING_TYPE_COUNTRY_ANALYSIS,
  NOTIFICATION_BRIEFING_TYPE_ELECTION_WATCH,
  NOTIFICATION_BRIEFING_TYPE_CALENDAR_EVENTS,
  NOTIFICATION_BRIEFING_TYPE_REGIONAL_OUTLOOK,
  NOTIFICATION_BRIEFING_TYPE_CYBER_WEEKLY,
  NOTIFICATION_BRIEFING_TYPE_CYBER_MONTHLY,
  NOTIFICATION_TYPE_SAVED_SEARCH,
  NOTIFICATION_ASSET_TYPE_RISK_RATING,
  NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT,
  NOTIFICATION_ASSET_TYPE_HOTSPOT,
  NOTIFICATION_ASSET_TYPE_SAVED_SEARCH,
} from "@/common/constants/notifications";
import { getTargets } from "@/services/targets/target.service";

export default {
  name: "NotificationsStepComms",
  components: {
    UserShareListEditor: () =>
      import("@/components/alerts/wizard/userShareList/UserShareListEditor"),
    TimeSelector: () => import("@/components/common/TimeSelector"),
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const daysOfWeek = DAYS_OF_WEEK.map((day) => {
      return day.name;
    });
    return {
      errorMessageName: null,
      errorMessageExpiration: null,
      frequencies: NOTIFICATION_FREQUENCY_OPTIONS,
      frequencyDaily: NOTIFICATION_FREQUENCY_DAILY,
      frequencyWeekly: NOTIFICATION_FREQUENCY_WEEKLY,
      daysOfWeek: daysOfWeek,
      expirations: NOTIFICATION_EXPIRATION_OPTIONS,
      targets: [],
      sharedWithInit: new Date().toISOString(),
      shareWithColleagues: false,
      accessGroupAllowed: null,
    };
  },
  computed: {
    ...mapGetters({
      notification: MODULE_PREFIX_NOTIFICATIONS + "notification",
    }),
    expirationMessage() {
      if (lodashGet(this.notification, "formatted.expirationDate")) {
        if (
          !dayjs(nowUtc()).isAfter(this.notification.formatted.expirationDate)
        ) {
          return `Expires ${getFormattedDate(
            this.notification.formatted.expirationDate
          )}`;
        } else {
          return `Alert expired on ${getFormattedDate(
            this.notification.formatted.expirationDate
          )}`;
        }
      }
      return null;
    },
    delivery: {
      get() {
        return lodashGet(this.notification, "formatted.delivery") || [];
      },
      set(value) {
        const notification = lodashCloneDeep(this.notification);
        notification.formatted = notification.formatted || {};
        notification.formatted.delivery = value;
        this.update(notification);
      },
    },
    numberOfTargetsSelected() {
      if (lodashGet(this.notification, "formatted.targets.length")) {
        return this.notification.formatted.targets.length;
      }
      return "0";
    },
    showSharedUsers() {
      return (
        this.shareWithColleagues &&
        (this.isNew || this.allowShareWithColleagues)
      );
    },
    isNew() {
      return !this.notification?.formatted?.id;
    },
    allowShareWithColleagues() {
      return this.notification?.formatted?.isOwner;
    },
    enforceEssentials() {
      return (
        this.notification.formatted.docType === NOTIFICATION_TRIPWIRE_TYPE_PULSE
      );
    },
    isCyberWeekly() {
      return (
        this.notification.formatted.docType ===
        NOTIFICATION_BRIEFING_TYPE_CYBER_WEEKLY
      );
    },
    isCalendarEvents() {
      return (
        this.notification.formatted.docType ===
        NOTIFICATION_BRIEFING_TYPE_CALENDAR_EVENTS
      );
    },
  },
  methods: {
    handleChangeName(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.name = value;
      this.update(notification);
    },
    handleChangeDayOfWeek(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.dayOfWeek = value;
      this.update(notification);
    },
    handleChangeTime(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.timeOfDay = value;
      this.update(notification);
    },
    handleChangeFrequency(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.frequency = value;
      notification.formatted.dayOfWeek = this.daysOfWeek[new Date().getDay()];
      const currHour = new Date().getHours();
      const nowHour = currHour > 9 ? currHour : `0${currHour}`;
      notification.formatted.timeOfDay = `${nowHour}:00`;
      if (
        notification.formatted.frequency.toUpperCase() ===
          NOTIFICATION_FREQUENCY_IMMEDIATELY ||
        notification.formatted.frequency.toUpperCase() ===
          NOTIFICATION_FREQUENCY_HOURLY ||
        notification.formatted.frequency.toUpperCase() ===
          NOTIFICATION_FREQUENCY_MONTHLY
      ) {
        notification.formatted.dayOfWeek = null;
        notification.formatted.timeOfDay = null;
      } else if (
        notification.formatted.frequency.toUpperCase() ===
        NOTIFICATION_FREQUENCY_DAILY
      ) {
        notification.formatted.dayOfWeek = null;
      }
      this.update(notification);
    },
    handleChangeExpiration(expiryId) {
      if (expiryId) {
        const expiry = lodashFind(this.expirations, ["id", expiryId]);
        if (expiry) {
          const notification = lodashCloneDeep(this.notification);
          notification.formatted = notification.formatted || {};
          notification.formatted.expiration = expiry.id;
          let apply = false;
          if (expiry.id === NOTIFICATION_EXPIRATION_ID_NEVER) {
            delete notification.formatted.expirationDate;
            apply = true;
          } else {
            if (expiry?.value && expiry?.type) {
              notification.formatted.expirationDate = dayjs()
                .add(expiry.value, expiry.type)
                .toISOString();
              apply = true;
            }
          }
          if (apply) {
            this.update(notification);
          }
        }
      }
    },
    handleChangeSharedList(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      if (value && value.length) {
        value.forEach((v) => {
          if (lodashHas(v, "selected")) {
            delete v.selected;
          }
        });
      }
      notification.formatted.sharedList = value;
      this.update(notification);
    },
    handleChangeTargets(value) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.targets = value;
      this.update(notification);
    },
    update(notification) {
      this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );
    },
    setFrequencies() {
      const frequencies = [];
      NOTIFICATION_FREQUENCY_OPTIONS.forEach((opt) => {
        if (lodashGet(this.notification, "formatted.docType")) {
          if (
            this.notification.formatted.docType !==
            NOTIFICATION_TYPE_SAVED_SEARCH
          ) {
            if (
              this.notification.formatted.docType ===
                NOTIFICATION_TRIPWIRE_TYPE_RISK_RATING ||
              this.notification.formatted.docType ===
                NOTIFICATION_TRIPWIRE_TYPE_EVACUATION ||
              this.notification.formatted.docType ===
                NOTIFICATION_TRIPWIRE_TYPE_PULSE ||
              this.notification.formatted.docType ===
                NOTIFICATION_TRIPWIRE_TYPE_PLANNED_EVENT ||
              this.notification.formatted.docType ===
                NOTIFICATION_ASSET_TYPE_RISK_RATING ||
              this.notification.formatted.docType ===
                NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT ||
              this.notification.formatted.docType ===
                NOTIFICATION_ASSET_TYPE_SAVED_SEARCH
            ) {
              if (opt.adverb === NOTIFICATION_FREQUENCY_IMMEDIATELY) {
                frequencies.push(opt);
              }
            } else if (
              this.notification.formatted.docType ===
              NOTIFICATION_BRIEFING_TYPE_CYBER_WEEKLY
            ) {
              if (opt.adverb === NOTIFICATION_FREQUENCY_WEEKLY) {
                frequencies.push(opt);
              }
            } else if (
              this.notification.formatted.docType ===
                NOTIFICATION_BRIEFING_TYPE_ELECTION_WATCH ||
              this.notification.formatted.docType ===
                NOTIFICATION_BRIEFING_TYPE_REGIONAL_OUTLOOK ||
              this.notification.formatted.docType ===
                NOTIFICATION_BRIEFING_TYPE_CYBER_MONTHLY
            ) {
              if (opt.adverb === NOTIFICATION_FREQUENCY_MONTHLY) {
                frequencies.push(opt);
              }
            } else if (
              this.notification.formatted.docType ===
                NOTIFICATION_BRIEFING_TYPE_COUNTRY_ANALYSIS ||
              this.notification.formatted.docType ===
                NOTIFICATION_TRIPWIRE_TYPE_HOTSPOT ||
              this.notification.formatted.docType ===
                NOTIFICATION_ASSET_TYPE_HOTSPOT
            ) {
              if (
                opt.adverb === NOTIFICATION_FREQUENCY_IMMEDIATELY ||
                opt.adverb === NOTIFICATION_FREQUENCY_DAILY
              ) {
                frequencies.push(opt);
              }
            } else if (
              this.notification.formatted.docType ===
              NOTIFICATION_BRIEFING_TYPE_CALENDAR_EVENTS
            ) {
              if (
                opt.adverb === NOTIFICATION_FREQUENCY_WEEKLY ||
                opt.adverb === NOTIFICATION_FREQUENCY_MONTHLY
              ) {
                frequencies.push(opt);
              }
            }
          } else {
            frequencies.push(opt);
          }
        } else {
          frequencies.push(opt);
        }
      });
      this.frequencies = frequencies;
    },
    setDefaultFrequencies() {
      let frequency;
      // Set default for cyber
      if (
        lodashGet(this.notification, "formatted.docType") &&
        this.notification.formatted.docType.toLowerCase().includes("cyber")
      ) {
        this.accessGroupAllowed = "Seerist_Cyber_Access";
        if (
          this.notification.formatted.docType ===
          NOTIFICATION_BRIEFING_TYPE_CYBER_WEEKLY
        ) {
          frequency = NOTIFICATION_FREQUENCY_WEEKLY;
        } else if (
          this.notification.formatted.docType ===
          NOTIFICATION_BRIEFING_TYPE_CYBER_MONTHLY
        ) {
          frequency = NOTIFICATION_FREQUENCY_MONTHLY;
        }
      }
      // Set default for election and regional
      if (
        lodashGet(this.notification, "formatted.docType") ===
          NOTIFICATION_BRIEFING_TYPE_ELECTION_WATCH ||
        lodashGet(this.notification, "formatted.docType") ===
          NOTIFICATION_BRIEFING_TYPE_REGIONAL_OUTLOOK
      ) {
        frequency = NOTIFICATION_FREQUENCY_MONTHLY;
      }
      // Set default for calendar events
      if (
        lodashGet(this.notification, "formatted.docType") ===
          NOTIFICATION_BRIEFING_TYPE_CALENDAR_EVENTS &&
        lodashGet(this.notification, "formatted.frequency") !==
          NOTIFICATION_FREQUENCY_WEEKLY &&
        lodashGet(this.notification, "formatted.frequency") !==
          NOTIFICATION_FREQUENCY_MONTHLY
      ) {
        frequency = NOTIFICATION_FREQUENCY_MONTHLY;
      }
      // Set default for country analysis
      if (
        lodashGet(this.notification, "formatted.docType") ===
          NOTIFICATION_BRIEFING_TYPE_COUNTRY_ANALYSIS &&
        lodashGet(this.notification, "formatted.frequency") !==
          NOTIFICATION_FREQUENCY_IMMEDIATELY &&
        lodashGet(this.notification, "formatted.frequency") !==
          NOTIFICATION_FREQUENCY_DAILY
      ) {
        frequency = NOTIFICATION_FREQUENCY_IMMEDIATELY;
      }
      if (
        frequency &&
        lodashGet(this.notification, "formatted.frequency") !== frequency
      ) {
        this.handleChangeFrequency(frequency);
      }
    },
  },
  watch: {
    shareWithColleagues() {
      if (!this.shareWithColleagues && this.notification) {
        const notification = lodashCloneDeep(this.notification);
        notification.formatted = notification.formatted || {};
        notification.formatted.sharedList = [];
        this.update(notification);
      }
    },
    notification: {
      async handler(newVal, oldVal) {
        this.shareWithColleagues = false;
        if (lodashGet(this.notification, "formatted.sharedList.length")) {
          this.shareWithColleagues = true;
        }
        this.accessGroupAllowed = null;
        if (
          newVal === null ||
          newVal === undefined ||
          oldVal === null ||
          oldVal === undefined ||
          !lodashIsEqual(newVal?.formatted?.targets, oldVal?.formatted?.targets)
        ) {
          this.targets = await getTargets();
        }
        this.setFrequencies();
        this.setDefaultFrequencies();
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    this.targets = [];
    this.errorMessageName = null;
    this.errorMessageExpiration = null;
  },
};
</script>

<style scoped>
.hyp-shared-listing {
  max-height: 200px;
  overflow-y: auto;
}
.hyp-time-day-label {
  color: rgba(0, 0, 0, 0.6);
  height: 20px;
  line-height: 20px;
  font-size: 12px;
}
.hyp-targets {
  position: relative;
  top: -4px;
}
.target-tooltip {
  width: 400px;
}
.target-type-header {
  text-transform: uppercase;
  font-size: 10px;
}
</style>
